import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { Check, Minus } from "lucide-react";
import { twMerge } from "tailwind-merge";

export type CheckboxState = "Checked" | "Indeterminate" | "Unchecked";

const BOX_CLASS = "m-1 flex size-3.5 shrink-0 items-center justify-center rounded-[2.25px] border-[1.5px]";

// Keyboard focus. A bronze border and glow.
const FOCUS_CLASS = "outline-none focus-visible:border-bronze-600 focus-visible:shadow-focus";

/** The box's colors. */
function toneClass(filled: boolean, disabled: boolean): string {
    if (disabled && filled) {
        return "cursor-not-allowed border-stone-400 bg-stone-400";
    }
    if (disabled) {
        return "cursor-not-allowed border-stone-400 bg-white";
    }
    if (filled) {
        return "cursor-pointer border-bronze-600 bg-bronze-600 hover:border-bronze-700 hover:bg-bronze-700 active:drop-shadow-pressed";
    }
    return "cursor-pointer border-neutral-500 bg-white hover:border-bronze-600 active:drop-shadow-pressed";
}

/** A tick when checked, a dash when mixed. */
const Mark = (props: { state: CheckboxState }) => {
    if (props.state === "Checked") {
        // Lucide's tick sits half a grid unit above its icon's middle, so nudging it down to center in the box.
        return <Check size={6} strokeWidth={1.5} absoluteStrokeWidth className="shrink-0 translate-y-[0.125px] stroke-white" />;
    }
    if (props.state === "Indeterminate") {
        return <Minus size={9} strokeWidth={1.5} absoluteStrokeWidth className="shrink-0 stroke-white" />;
    }
    return null;
};

interface CheckboxProps {
    className?: string;
    /** A boolean, or a CheckboxState for the indeterminate state. */
    checked: CheckboxState | boolean;
    /** Makes the checkbox interactive: focusable, toggled by Space, and announced as a checkbox. 
     * Without it, it's only a visual inside a row that handles the click. */
    onClick?: (e: React.MouseEvent) => void;
    disabled?: boolean;
    /** The checkbox's accessible name, when nothing beside it labels it. */
    ariaLabel?: string;
}

/**
 * Modeled after the design system in Figma.
 * @see https://www.figma.com/design/gufDjkozUfXf6IYe5dhndc/Design-System?node-id=1146-2942
 */
const Checkbox = ({ className, checked, onClick, disabled = false, ariaLabel }: CheckboxProps) => {
    let state: CheckboxState = checked ? "Checked" : "Unchecked";
    if (typeof checked === "string") {
        state = checked;
    }
    const boxClass = twMerge(BOX_CLASS, toneClass(state !== "Unchecked", disabled), className);

    // A decorative checkbox sits inside a row that is the real control, so it isn't focusable or announced.
    if (onClick === undefined) {
        return (
            <span aria-hidden className={boxClass}>
                <Mark state={state} />
            </span>
        );
    }

    return (
        <BaseCheckbox.Root
            checked={state === "Checked"}
            indeterminate={state === "Indeterminate"}
            disabled={disabled}
            aria-label={ariaLabel}
            onClick={disabled ? undefined : onClick}
            className={twMerge(boxClass, FOCUS_CLASS)}
        >
            <Mark state={state} />
        </BaseCheckbox.Root>
    );
};

export default Checkbox;
